import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnInit,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Polygon } from 'geojson';

import {
    ColorDropdownComponent,
    ProjectColors,
} from '@connect-services/client/components/color-dropdown/color-dropdown.component';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ProjectionOption, ProjectService } from '@connect-services/client/services/project.service';
import {
    ProjectCreationEditionForm,
    ShapingProjectService,
} from '@connect-services/client/services/project/shaping/shaping-project.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { Project } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { FormService } from '@shared/client/services/form.service';

export type ModalData = {
    bbox: Polygon | null;
    color?: ProjectColors;
    name?: string;
    projection?: Project.ProjectionEnum;
    title: string;
};

export type ProjectShapingModalResult = {
    creationEditionForm?: ProjectCreationEditionForm;
    mode: ProjectModalCloseMode;
};

export enum ProjectModalCloseMode {
    BBOX_SHAPING,
    SAVE,
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ColorDropdownComponent, ComplexModalTemplate, LoadingComponent, RessourcesModule],
    selector: 'app-project-create-modal',
    templateUrl: './create-edit-project-modal.component.html',
})
export class CreateEditProjectModalComponent implements AfterViewInit, OnInit {
    public readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');
    public readonly projections: WritableSignal<ProjectionOption[] | null> = signal(null);
    public readonly router = inject(Router);

    protected readonly ButtonType = ButtonType;
    protected data: ModalData = inject(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<CreateEditProjectModalComponent>);
    protected readonly formService = inject(FormService);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly shapingProjectService = inject(ShapingProjectService);

    private readonly projectService = inject(ProjectService);

    public ngAfterViewInit(): void {
        this.nameInput().nativeElement.focus();
    }

    public ngOnInit(): void {
        this.shapingProjectService.setDefaultData(this.data);
        this.initProjections();
    }

    public onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }
        this.dialogRef.close({
            creationEditionForm: this.shapingProjectService.projectForm,
            mode: ProjectModalCloseMode.SAVE,
        });
    }

    public setProjectBbox(): void {
        this.dialogRef.close({
            mode: ProjectModalCloseMode.BBOX_SHAPING,
        });
    }

    private initProjections(): void {
        const projections: ProjectionOption[] = this.projectService.getProjectionSelection();

        this.projections.set(projections);
    }
}
